import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { gql } from '@apollo/client';
import { LsqDataSource } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { first, map, shareReplay } from 'rxjs/operators';

export interface RelationshipEnrollmentTotalsGql {
    enrolledCount: number;
    notEnrolledCount: number;
    notEnrolledInvitedCount: number;
    totalCount: number;
}

export interface SupplierOverviewRequest {
    buyerKeys?: string[];
}

class SupplierOverviewDataSource extends LsqDataSource<RelationshipEnrollmentTotalsGql> {
    public dispatchRequest: (req: SupplierOverviewRequest) => void;

    public constructor(init: Partial<SupplierOverviewDataSource>) {
        super(init);
        this.dispatchRequest = init.dispatchRequest;
    }
}
export type { SupplierOverviewDataSource }; // We don't want others instantiating this class

@Injectable({ providedIn: 'root' })
export class SupplierOverviewDataSourceFactory {
    constructor(private _http: HttpClient, @Inject(SETTINGS) private _settings: ISettings) {}

    public createSupplierOverviewDataSource({
        buyerKeys = []
    }: SupplierOverviewRequest): SupplierOverviewDataSource {
        const data$ = new BehaviorSubject<RelationshipEnrollmentTotalsGql>({
            enrolledCount: 0,
            notEnrolledCount: 0,
            notEnrolledInvitedCount: 0,
            totalCount: 0
        });
        const error$ = new Subject<unknown>();
        const loading$ = new BehaviorSubject<boolean>(true);
        const noData$ = combineLatest([data$, loading$]).pipe(
            map(([data, loading]): boolean => !loading && !data),
            shareReplay(1)
        );
        const dispatchRequest = (req: SupplierOverviewRequest) => {
            this._http
                .post<{
                    data: { relationshipEnrollmentTotals: RelationshipEnrollmentTotalsGql };
                    errors?: any[];
                }>(this._settings.httpApiServiceNameUrlMap.financingGql + '/v1/query', {
                    query: gql`
                        query GetSupplierOverview($buyerIds: [ID!]!) {
                            relationshipEnrollmentTotals(input: { buyerIds: $buyerIds }) {
                                enrolledCount
                                notEnrolledCount
                                notEnrolledInvitedCount
                                totalCount
                            }
                        }
                    `.loc.source.body,
                    variables: {
                        buyerIds: req.buyerKeys
                    }
                })
                .pipe(first())
                .subscribe(
                    (respBody) => {
                        if (respBody.errors) {
                            error$.next(respBody.errors);
                        } else {
                            data$.next(respBody.data.relationshipEnrollmentTotals);
                        }
                    },
                    (error) => error$.next(error),
                    () => loading$.next(false)
                );
        };

        // Make an API call on init
        dispatchRequest({ buyerKeys });

        return new SupplierOverviewDataSource({
            data$,
            noData$,
            loading$,
            error$,
            dispatchRequest
        });
    }
}
